import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { RefreshCw, MailOpen, MousePointerClick, TrendingUp } from "lucide-react";
import { toast } from "sonner";

const pct = (n) => `${Math.round((n || 0) * 100)}%`;
const timeAgo = (iso) => {
  if (!iso) return "—";
  const s = Math.max(1, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
};

/**
 * DigestAnalyticsCard — open + click rates per director on the weekly board
 * digest email. Reads from /api/admin/board-digest/analytics.
 */
export function DigestAnalyticsCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/board-digest/analytics");
      setData(data);
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Failed to load digest analytics");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <Card className="p-5 border-white/10 bg-card" data-testid="digest-analytics-card">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="overline">Digest analytics</div>
          <div className="text-base font-semibold font-display mt-0.5 inline-flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-emerald-400" /> Who&apos;s reading the weekly digest
          </div>
          <div className="text-xs text-zinc-500 mt-1">
            Open + click rates per director, tracked via per-recipient pixels and click-wrapped links.
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={load} disabled={loading} className="h-7 px-2" data-testid="digest-analytics-refresh">
          <RefreshCw className={"h-3 w-3 " + (loading ? "animate-spin" : "")} />
        </Button>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
            <div className="rounded-md border border-white/5 px-3 py-2">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Total sent</div>
              <div className="text-xl font-semibold tabular-nums text-white" data-testid="analytics-total-sent">{data.total_sent}</div>
            </div>
            <div className="rounded-md border border-white/5 px-3 py-2">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Total opens</div>
              <div className="text-xl font-semibold tabular-nums text-emerald-300" data-testid="analytics-total-opens">{data.total_opens}</div>
            </div>
            <div className="rounded-md border border-white/5 px-3 py-2">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Open rate</div>
              <div className="text-xl font-semibold tabular-nums text-emerald-300" data-testid="analytics-open-rate">{pct(data.overall_open_rate)}</div>
            </div>
            <div className="rounded-md border border-white/5 px-3 py-2">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Click rate</div>
              <div className="text-xl font-semibold tabular-nums text-orange-300" data-testid="analytics-click-rate">{pct(data.overall_click_rate)}</div>
            </div>
          </div>

          {(data.recipients || []).length === 0 ? (
            <div className="rounded-md border border-dashed border-white/10 p-6 text-center text-xs text-zinc-500" data-testid="analytics-empty">
              No digest runs yet. Enable the digest and directors&apos; opens will appear here.
            </div>
          ) : (
            <div className="overflow-hidden rounded-md border border-white/5">
              <table className="w-full text-xs">
                <thead className="bg-white/[0.03]">
                  <tr>
                    <th className="text-left px-3 py-2 text-zinc-400">Recipient</th>
                    <th className="text-right px-3 py-2 text-zinc-400">Sent</th>
                    <th className="text-right px-3 py-2 text-zinc-400">Opens</th>
                    <th className="text-right px-3 py-2 text-zinc-400">Clicks</th>
                    <th className="text-right px-3 py-2 text-zinc-400">Open rate</th>
                    <th className="text-right px-3 py-2 text-zinc-400">Last activity</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recipients.map((r) => (
                    <tr key={r.email} className="border-t border-white/5" data-testid={`analytics-recipient-${r.email}`}>
                      <td className="px-3 py-1.5">
                        <div className="text-zinc-200 truncate">{r.name || r.email}</div>
                        <div className="text-[11px] text-zinc-500 truncate">{r.email} · {r.role || "board"}</div>
                      </td>
                      <td className="px-3 py-1.5 text-right text-zinc-400 tabular-nums">{r.sent || 0}</td>
                      <td className="px-3 py-1.5 text-right text-emerald-300 tabular-nums inline-flex items-center gap-1">
                        <MailOpen className="h-3 w-3" /> {r.opens || 0}
                      </td>
                      <td className="px-3 py-1.5 text-right text-orange-300 tabular-nums inline-flex items-center gap-1">
                        <MousePointerClick className="h-3 w-3" /> {r.clicks || 0}
                      </td>
                      <td className="px-3 py-1.5 text-right">
                        <Badge variant="outline" className={
                          (r.open_rate || 0) >= 0.75 ? "border-emerald-500/40 text-emerald-300" :
                          (r.open_rate || 0) >= 0.25 ? "border-amber-500/40 text-amber-300" :
                          "border-red-500/40 text-red-300"
                        }>
                          {pct(r.open_rate)}
                        </Badge>
                      </td>
                      <td className="px-3 py-1.5 text-right text-[11px] text-zinc-500">
                        {timeAgo(r.last_click_at || r.last_open_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

export default DigestAnalyticsCard;
